import React from "react";
import { useRef, useState, useEffect, useCallback } from "react";

import ConfigData from "../../ConfigurationData/ConfigData.json";
import { Buffer } from "buffer";

import { DropDownList } from "@progress/kendo-react-dropdowns";
import {
  RadioGroup,
  Checkbox,
  NumericTextBox,
} from "@progress/kendo-react-inputs";
// import { Button } from "@progress/kendo-react-buttons";

import UpdateCreditCardOnFile from "./UpdateCreditCardOnFile";

import classes from "./MembershipSettings.module.css";
import {
  getTokenFromStorage,
  getUserId,
  getToken,
} from "../../api/RestServices";

// ** MUI Imports
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import CardHeader from "@mui/material/CardHeader";
import Typography from "@mui/material/Typography";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import { Link } from "@mui/material";
import { Form, Field, FormElement, Text } from "@progress/kendo-react-form";
import MuiFormControlLabel from "@mui/material/FormControlLabel";

const FormControlLabel = styled(MuiFormControlLabel)(({ theme }) => ({
  "& .MuiFormControlLabel-label": {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
}));

// Pure address-block builder (2026-09-23, Seetha's typed-or-picked address ruling). Exported so
// it can be exercised directly by a throwaway unit test without mounting the whole component.
// Escapes every value via JSON.stringify instead of the old manual `'"' + value + '"'`
// quote-wrapping, which broke outright on any double-quote or backslash in the value -- not
// reachable before (values only ever came from Google or the DB), but reachable now that typed
// text is allowed. Key names are unchanged so the API contract doesn't move.
export function buildAddressDetailsJson(addr) {
  return JSON.stringify({
    SelectedAddress1: (addr && addr.SelectedAddress1) || "",
    SelectedAddress2: (addr && addr.SelectedAddress2) || "",
    SelectedCity: (addr && addr.SelectedCity) || "",
    SelectedState: (addr && addr.SelectedState) || "",
    SelectedZipcode: (addr && addr.SelectedZipcode) || "",
    SelectedCountry: (addr && addr.Country) || "",
  });
}

// Req E (2026-09-23): "US"/"USA"/"United States"/"United States of America" all normalise to the
// one canonical string this file already checks against elsewhere ("United States"). Anything
// else (Canada, Mexico, etc.) passes through unchanged, trimmed only.
export function normalizeCountry(country) {
  const trimmed = (country || "").trim();
  const upper = trimmed.toUpperCase();
  if (
    upper === "US" ||
    upper === "USA" ||
    upper === "UNITED STATES" ||
    upper === "UNITED STATES OF AMERICA"
  ) {
    return "United States";
  }
  return trimmed;
}

export function isUnitedStates(country) {
  return normalizeCountry(country) === "United States";
}

// Req E: one consistent State format for the US -- the 2-letter code -- regardless of whether it
// came from a DB prefill (which can hold the full spelled-out name, e.g. "Virginia" -- the exact
// gap in Seetha's screenshot), a Google pick (short_name is already 2 letters), or free typing.
// Non-US addresses are left alone -- "State/Province" is free text outside the US.
const US_STATE_ABBREVIATIONS = {
  alabama: "AL",
  alaska: "AK",
  arizona: "AZ",
  arkansas: "AR",
  california: "CA",
  colorado: "CO",
  connecticut: "CT",
  delaware: "DE",
  "district of columbia": "DC",
  florida: "FL",
  georgia: "GA",
  hawaii: "HI",
  idaho: "ID",
  illinois: "IL",
  indiana: "IN",
  iowa: "IA",
  kansas: "KS",
  kentucky: "KY",
  louisiana: "LA",
  maine: "ME",
  maryland: "MD",
  massachusetts: "MA",
  michigan: "MI",
  minnesota: "MN",
  mississippi: "MS",
  missouri: "MO",
  montana: "MT",
  nebraska: "NE",
  nevada: "NV",
  "new hampshire": "NH",
  "new jersey": "NJ",
  "new mexico": "NM",
  "new york": "NY",
  "north carolina": "NC",
  "north dakota": "ND",
  ohio: "OH",
  oklahoma: "OK",
  oregon: "OR",
  pennsylvania: "PA",
  "rhode island": "RI",
  "south carolina": "SC",
  "south dakota": "SD",
  tennessee: "TN",
  texas: "TX",
  utah: "UT",
  vermont: "VT",
  virginia: "VA",
  washington: "WA",
  "west virginia": "WV",
  wisconsin: "WI",
  wyoming: "WY",
  "puerto rico": "PR",
};

export function normalizeState(state, country) {
  const trimmed = (state || "").trim();
  if (!trimmed || !isUnitedStates(country)) return trimmed;
  if (trimmed.length === 2) return trimmed.toUpperCase();
  const abbr = US_STATE_ABBREVIATIONS[trimmed.toLowerCase()];
  return abbr || trimmed;
}

// Req B (2026-09-23): match Google address_components by TYPE, not fixed array position -- the
// old code indexed address[0]/address[1]/... positionally and picked among branches keyed off
// address[2].types[0], which mis-parsed any address whose component order differs from the
// "usual" case (apartment/subpremise present, a rural address with no locality, etc.) and
// referenced the non-existent `address.Length` (JS is case-sensitive -- should be `.length`, so
// that one zip-code branch was always broken). This is the same logic Worker-B's first pass used
// inside place_changed, now extracted so it's shared with the Save-time Google-split path (Req C)
// and independently unit-testable.
export function parseAddressComponents(components) {
  const list = components || [];
  const byType = (type) =>
    list.find((c) => c.types && c.types.includes(type));

  const streetNumber = byType("street_number");
  const route = byType("route");
  const subpremise = byType("subpremise");
  const locality =
    byType("locality") || byType("postal_town") || byType("sublocality_level_1");
  const state = byType("administrative_area_level_1");
  const zip = byType("postal_code");
  const countryComp = byType("country");

  const country = normalizeCountry(countryComp ? countryComp.long_name : "");

  return {
    SelectedAddress1: [streetNumber, route]
      .filter(Boolean)
      .map((c) => c.long_name)
      .join(" "),
    SelectedAddress2: subpremise ? subpremise.long_name : "",
    SelectedCity: locality ? locality.long_name : "",
    SelectedState: normalizeState(state ? state.short_name : "", country),
    SelectedZipcode: zip ? zip.long_name : "",
    Country: country,
  };
}

// Single one-line summary string, used both for the "Mailing Address" box display and to detect
// whether the box's live text still matches the last picked/prefilled address (Req C).
export function buildOneLineAddress(addr) {
  if (!addr) return "";
  return [
    addr.SelectedAddress1,
    addr.SelectedAddress2,
    addr.SelectedCity,
    addr.SelectedState,
    addr.SelectedZipcode,
    addr.Country,
  ]
    .filter((part) => part && part !== "")
    .join(", ");
}

// Req C's "confident single result" gate, and also the validation rule (spec item 7, unchanged):
// Address1/City/Country always required; State+Zip required only when Country is the US. Shared
// so Save-time Google-split acceptance and form validation can never disagree with each other.
export function hasRequiredAddressParts(addr) {
  if (!addr) return false;
  const isUS = isUnitedStates(addr.Country);
  return !!(
    addr.SelectedAddress1 &&
    addr.SelectedAddress1.trim() !== "" &&
    addr.SelectedCity &&
    addr.SelectedCity.trim() !== "" &&
    addr.Country &&
    addr.Country.trim() !== "" &&
    (!isUS ||
      (addr.SelectedState &&
        addr.SelectedState.trim() !== "" &&
        addr.SelectedZipcode &&
        addr.SelectedZipcode.trim() !== ""))
  );
}

const MembershipSettings = (props) => {
  let urlAPI = ConfigData.apiURL;
  let appId = ConfigData.appId;
  // let UserID = localStorage.getItem('UserId');
  const [UserID, setUserID] = useState(props.UserID);
  const [isAddressInternational, setIsAddressInternational] = useState(false);
  const [addressDetailsValue, setAddressDetailsValue] = useState("");
  const [isFirstAddressValue, setIsFirstAddressValue] = useState(true);

  const [modelAddressDetails, setModelAddressDetails] = useState({});
  // Controlled address fields (2026-09-23, Seetha's ruling: show the existing address, offer
  // Google suggestions as you type, and save whichever text is on screen -- picked or typed).
  // This is the single source of truth read by Save; modelAddressDetails.AddressDetails is still
  // mutated in parallel (existing pattern in this file) purely so the Country-driven
  // isAddressInternational render branches elsewhere keep working unchanged.
  const [addr, setAddr] = useState({
    SelectedAddress1: "",
    SelectedAddress2: "",
    SelectedCity: "",
    SelectedState: "",
    SelectedZipcode: "",
    Country: "",
  });
  // Req A/D (2026-09-23, Seetha's rejection of the previous layout): the 6 explicit fields are
  // hidden by default -- only the single Mailing-Address-style box shows. "Edit" reveals them.
  const [showEditFields, setShowEditFields] = useState(false);
  const [modelMembershipDetails, setModelMembershipDetails] = useState({});
  const [modelMagazineSelectionDetails, setModelMagazineSelectionDetails] =
    useState({});
  const [modelAreaDesignationDetails, setModelAreaDesignationDetails] =
    useState({});
  const [modelYoungRiderProgramDetails, setModelYoungRiderProgramDetails] =
    useState({});
  const [modelAdultRiderProgramDetails, setModelAdultRiderProgramDetails] =
    useState({});
  const [modelAnnualDonationDetails, setModelAnnualDonationDetails] = useState(
    {}
  );
  const [modelAutoRenewMembershipDetails, setModelAutoRenewMembershipDetails] =
    useState({});
  const [modelAmateurDetails, setModelAmateurDetails] = useState({});

  const [isShowCardDetailsPopup, setShowCardDetailsPopup] = useState(false);
  const [isPaymentDone, setIsPaymentDone] = useState(false);

  const [dataConfirmAddressErrorMessage, setDataConfirmAddressErrorMessage] =
    useState({});
  const [dataMembershipTypeErrorMessage, setDataMembershipTypeErrorMessage] =
    useState({});
  const [dataAmateurErrorMessage, setDataAmateurErrorMessage] = useState({});
  const [
    dataMagazineSelectionErrorMessage,
    setDataMagazineSElectionErrorMessage,
  ] = useState({});
  const [
    dataAreaDesignationErrorMessage,
    setDataAreaDesignationMonthErrorMessage,
  ] = useState({});
  const [
    dataYoungRiderProgramErrorMessage,
    setDataYoungRiderProgramErrorMessage,
  ] = useState({});
  const [
    dataAdultRiderProgramErrorMessage,
    setDataAdultRiderProgramErrorMessage,
  ] = useState({});
  const [
    dataAnnualDonationAmountErrorMessage,
    setDataAnnualDonationAmountErrorMessage,
  ] = useState({});
  const [data, setData] = useState();
  const autoCompleteRef = useRef();
  const inputRef = useRef();
  const options = {
    fields: ["address_components", "geometry", "icon", "name"],
  };
  const google_api = ConfigData.apikey;

  useEffect(() => {
    if (props.objModelMembershipDetails !== null) {
      if (props.objModelMembershipDetails.AddressDetails !== null) {
        // Req 1/E (2026-09-23): rebuilt this block to normalise Country/State on the way in --
        // this is exactly the bug in Seetha's screenshot (DB prefill showed "Virginia" spelled
        // out, unlike a Google pick's 2-letter short_name). One normalised object now feeds the
        // controlled `addr` state, the mutate-in-place modelAddressDetails.AddressDetails (kept
        // for the downstream Country-driven render branches, same as before), and the one-line
        // display string the "Mailing Address" box shows -- so all three always agree.
        const rawCountry =
          props.objModelMembershipDetails.AddressDetails.Country || "";
        const normalizedCountry = normalizeCountry(rawCountry);
        const prefillAddr = {
          SelectedAddress1:
            props.objModelMembershipDetails.AddressDetails.Address1 || "",
          SelectedAddress2:
            props.objModelMembershipDetails.AddressDetails.Address2 || "",
          SelectedCity:
            props.objModelMembershipDetails.AddressDetails.City || "",
          SelectedState: normalizeState(
            props.objModelMembershipDetails.AddressDetails.State || "",
            normalizedCountry
          ),
          SelectedZipcode:
            props.objModelMembershipDetails.AddressDetails.Zipcode || "",
          Country: normalizedCountry,
        };

        modelAddressDetails.AddressDetails = { ...prefillAddr };

        setAddr(prefillAddr);
        setAddressDetailsValue(buildOneLineAddress(prefillAddr));
        setIsFirstAddressValue(false);

        // Same US/Canada/Mexico + paid-membership-type branching as before this change. Left
        // inline (not switched to the shared updateIsAddressInternational helper used by the
        // newer code paths below) because modelMembershipDetails.MembershipTypeDetails isn't
        // populated until later in this SAME effect pass (see the next block) -- this ordering
        // quirk already existed before today's rework; preserving it exactly rather than
        // changing behaviour on a page Seetha hasn't asked to have that part re-reviewed.
        if (normalizedCountry === "United States") {
          setIsAddressInternational(false);
        } else if (
          normalizedCountry === "Canada" &&
          modelMembershipDetails &&
          modelMembershipDetails.MembershipTypeDetails &&
          modelMembershipDetails.MembershipTypeDetails
            .SelectedMembershipTypeValue !== "0"
        ) {
          setIsAddressInternational(true);
        } else if (
          normalizedCountry === "Mexico" &&
          modelMembershipDetails &&
          modelMembershipDetails.MembershipTypeDetails &&
          modelMembershipDetails.MembershipTypeDetails
            .SelectedMembershipTypeValue !== "0"
        ) {
          setIsAddressInternational(true);
        } else if (
          modelMembershipDetails &&
          modelMembershipDetails.MembershipTypeDetails &&
          modelMembershipDetails.MembershipTypeDetails
            .SelectedMembershipTypeValue !== "0"
        ) {
          setIsAddressInternational(true);
        }
      }

      modelMembershipDetails.MembershipTypeDetails = {};
      if (props.objModelMembershipDetails.MembershipTypeDetails !== null) {
        modelMembershipDetails.MembershipTypeDetails.SelectedMembershipTypeName =
          props.objModelMembershipDetails.MembershipTypeDetails.MembershipTypeSelection.MembershipTypeName;
        modelMembershipDetails.MembershipTypeDetails.SelectedMembershipTypeValue =
          props.objModelMembershipDetails.MembershipTypeDetails.MembershipTypeSelection.MembershipTypeValue;
      }

      modelMagazineSelectionDetails.MagazineSelectionDetails = {};
      if (props.objModelMembershipDetails.MagazineSelectionDetails !== null) {
        modelMagazineSelectionDetails.MagazineSelectionDetails.SelectedMagazineSelectionName =
          props.objModelMembershipDetails.MagazineSelectionDetails.MagazineSelection.MagazineSelectionName;
        modelMagazineSelectionDetails.MagazineSelectionDetails.SelectedMagazineSelectionValue =
          props.objModelMembershipDetails.MagazineSelectionDetails.MagazineSelection.MagazineSelectionValue;
      }

      modelAreaDesignationDetails.AreaDesignationDetails = {};
      if (props.objModelMembershipDetails.AreaDesignationDetails !== null) {
        modelAreaDesignationDetails.AreaDesignationDetails.SelectedAreaDesignationName =
          props.objModelMembershipDetails.AreaDesignationDetails.AreaDesignationSelection.AreaDesignationName;
        modelAreaDesignationDetails.AreaDesignationDetails.SelectedAreaDesignationValue =
          props.objModelMembershipDetails.AreaDesignationDetails.AreaDesignationSelection.AreaDesignationValue;
      }

      modelYoungRiderProgramDetails.YoungRiderProgramDetails = {};
      if (props.objModelMembershipDetails.YoungRiderProgramDetails !== null) {
        modelYoungRiderProgramDetails.YoungRiderProgramDetails.SelectedYoungRiderProgramDetailsValue =
          props.objModelMembershipDetails.YoungRiderProgramDetails.YoungRiderProgramDetailsSelection;
      }

      modelAdultRiderProgramDetails.AdultRiderProgramDetails = {};
      if (props.objModelMembershipDetails.AdultRiderProgramDetails !== null) {
        modelAdultRiderProgramDetails.AdultRiderProgramDetails.SelectedAdultRiderProgramDetailsValue =
          props.objModelMembershipDetails.AdultRiderProgramDetails.AdultRiderProgramDetailsSelection;
      }

      modelAnnualDonationDetails.AnnualDonationDetails = {};
      if (props.objModelMembershipDetails.AnnualDonationDetails !== null) {
        modelAnnualDonationDetails.AnnualDonationDetails.SelectedAnnualDonationName =
          props.objModelMembershipDetails.AnnualDonationDetails.AnnualDonationDetailsSelection.AnnualDonationDetailsName;
        modelAnnualDonationDetails.AnnualDonationDetails.SelectedAnnualDonationValue =
          props.objModelMembershipDetails.AnnualDonationDetails.AnnualDonationDetailsSelection.AnnualDonationDetailsValue;
        modelAnnualDonationDetails.AnnualDonationDetails.SelectedAnnualDonationAmount =
          props.objModelMembershipDetails.AnnualDonationDetails.AnnualDonationAmount;
      }

      modelAutoRenewMembershipDetails.AutoRenewMembershipDetails = {};
      if (props.objModelMembershipDetails.AutoRenewMembershipDetails !== null) {
        // FIX 2026-09-30: the API (Model_OS_PortalMembershipAutoRenewMembershipDetails) returns
        // the member's current setting as AutoRenewMembershipValue - it has NO
        // SelectedAutoRenewMembership field, so this used to start as undefined and every Save
        // (unless the checkbox was clicked) sent "SelectedAutoRenewMembership":0 and silently
        // turned auto-renewal OFF for members who had it ON.
        const autoRenewFromApi = props.objModelMembershipDetails.AutoRenewMembershipDetails;
        modelAutoRenewMembershipDetails.AutoRenewMembershipDetails.SelectedAutoRenewMembership =
          autoRenewFromApi.SelectedAutoRenewMembership !== undefined &&
          autoRenewFromApi.SelectedAutoRenewMembership !== null
            ? autoRenewFromApi.SelectedAutoRenewMembership
            : !!autoRenewFromApi.AutoRenewMembershipValue;
      }

      modelAmateurDetails.AmateurTypeDetails = {};
      if (
        props.objModelMembershipDetails.AmateurTypeDetails !== null &&
        props.objModelMembershipDetails.AmateurTypeDetails
          .AmateurDetailsSelection !== null
      ) {
        modelAmateurDetails.AmateurTypeDetails.SelectedAmateurTypeValue =
          props.objModelMembershipDetails.AmateurTypeDetails.AmateurDetailsSelection;
      }
    }

    // Google Places binding guard (2026-09-23): only create the Autocomplete once (this effect's
    // deps re-run it on every modelAddressDetails/modelMembershipDetails change, which previously
    // stacked a new Autocomplete instance + .pac-container on every state update), and only once
    // window.google.maps.places has actually loaded (a blocked/slow Maps script, e.g. Edge Strict
    // tracking prevention, would otherwise throw here and crash the whole effect/page). If either
    // guard fails, bail out for this render; the effect re-runs on the next relevant state change.
    if (
      autoCompleteRef.current ||
      !inputRef.current ||
      !window.google?.maps?.places
    ) {
      return;
    }

    autoCompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      options
    );

    autoCompleteRef.current.addListener("place_changed", async function () {
      const place = await autoCompleteRef.current.getPlace();
      if (place && place.address_components) {
        // Req B (2026-09-23): fill the hidden addr parts by component TYPE via the shared
        // parseAddressComponents helper (same logic as before, now also normalising
        // Country/State -- Req E).
        const newAddr = parseAddressComponents(place.address_components);

        setAddr(newAddr);

        // Keep modelAddressDetails.AddressDetails in sync (mutate-in-place, existing pattern in
        // this file) so the Country-driven isAddressInternational render branches elsewhere,
        // which read modelAddressDetails.AddressDetails.Country directly, keep working
        // unchanged.
        modelAddressDetails.AddressDetails = { ...newAddr };

        const displayText = buildOneLineAddress(newAddr);
        setAddressDetailsValue(displayText);
        // Overwrite whatever Google's widget itself just wrote into the input (its own
        // formatted_address text, which won't byte-for-byte match our own one-line format) so
        // the box and `addressDetailsValue` always agree -- this is what lets Save (Req C)
        // reliably tell "unchanged since pick" apart from "member edited/retyped after picking"
        // without an unnecessary extra Google round-trip on the common pick-then-immediately-
        // save path.
        if (inputRef.current) inputRef.current.value = displayText;

        updateIsAddressInternational(newAddr.Country);
      }
    });
  }, [modelAddressDetails, modelMembershipDetails]);

  // Sync the plain <input>'s displayed text whenever addressDetailsValue changes from an
  // external source (prefill arriving async, or a pick) -- Req 1's "existing address needs to
  // be displayed" gap: the previous defaultValue-based box stayed blank if the GET response
  // arrived after mount, since defaultValue only applies once. This is a plain (non-MUI)
  // uncontrolled input now (Req A -- see the render section), so writing .value directly here
  // does not fight React and does not require destroying/recreating the bound Autocomplete
  // instance the way a key-based remount would have.
  useEffect(() => {
    if (inputRef.current) inputRef.current.value = addressDetailsValue || "";
  }, [addressDetailsValue]);

  // Shared by the prefill/pick/typed-edit/Google-split paths below so the US/Canada/Mexico +
  // paid-membership-type branching only lives in one place going forward (the original prefill
  // effect above keeps its own inline copy -- see the comment there for why). Wrapped in
  // useCallback (same dependency this file's other address effect already lists) so referencing
  // it from inside the place_changed effect above doesn't add to that effect's already-long
  // pre-existing exhaustive-deps warning.
  const updateIsAddressInternational = useCallback(
    (country) => {
      if (isUnitedStates(country)) {
        setIsAddressInternational(false);
        return;
      }
      const membershipTypeSelected =
        modelMembershipDetails &&
        modelMembershipDetails.MembershipTypeDetails &&
        modelMembershipDetails.MembershipTypeDetails
          .SelectedMembershipTypeValue !== "0";
      if (
        (country === "Canada" || country === "Mexico") &&
        membershipTypeSelected
      ) {
        setIsAddressInternational(true);
      } else if (membershipTypeSelected) {
        setIsAddressInternational(true);
      } else {
        setIsAddressInternational(false);
      }
    },
    [modelMembershipDetails]
  );

  // Req D / pick-then-edit (2026-09-23): the 6 Edit-section fields are controlled and every
  // onChange writes straight into `addr`, so whatever's on screen there at Save time -- a pick
  // the member then hand-edited, or fields typed directly -- is exactly what gets sent.
  // modelAddressDetails.AddressDetails is kept in sync for the same reason as in place_changed
  // (downstream Country-driven render branches read it directly). Deliberately does NOT
  // normalise on every keystroke (Req E) -- that would fight the member mid-type; see
  // onBlurAddressField below for when normalisation actually happens.
  const onChangeAddressField = (field) => (event) => {
    const value = event.target.value;
    setAddr((prev) => ({ ...prev, [field]: value }));
    modelAddressDetails.AddressDetails[field] = value;

    if (field === "Country") {
      updateIsAddressInternational(value);
    }
  };

  // Req E (2026-09-23): normalise Country ("US"/"USA"/"United States"->"United States") and
  // State (full name -> 2-letter code, US only) once the member leaves the field, not while
  // they're still typing it.
  const onBlurAddressField = (field) => (event) => {
    const value = event.target.value;
    if (field === "Country") {
      const normalized = normalizeCountry(value);
      setAddr((prev) => ({
        ...prev,
        Country: normalized,
        SelectedState: normalizeState(prev.SelectedState, normalized),
      }));
      modelAddressDetails.AddressDetails.Country = normalized;
      modelAddressDetails.AddressDetails.SelectedState = normalizeState(
        modelAddressDetails.AddressDetails.SelectedState,
        normalized
      );
      updateIsAddressInternational(normalized);
    } else if (field === "SelectedState") {
      setAddr((prev) => ({
        ...prev,
        SelectedState: normalizeState(value, prev.Country),
      }));
      modelAddressDetails.AddressDetails.SelectedState = normalizeState(
        value,
        modelAddressDetails.AddressDetails.Country
      );
    }
  };

  // Req D: toggles the "Edit" section. Hidden by default, per Seetha's ruling that the 6
  // separate fields must not show unless asked for.
  const onToggleEditFields = (event) => {
    event.preventDefault();
    setShowEditFields((prev) => !prev);
  };

  function onCardDetailsClick(event) {
    event.preventDefault();
    setShowCardDetailsPopup(!isShowCardDetailsPopup);
  }

  function updateShowCardDetailsPopup(childShowCardDetailsPopup) {
    setShowCardDetailsPopup(childShowCardDetailsPopup);
  }

  function updateIsPaymentDone(childIsPaymentDone) {
    setIsPaymentDone(childIsPaymentDone);
  }

  const onChangeMembershipType = (event) => {
    let strSelectedMembershipTypeName = "";
    let strSelectedMembershipTypeValue = "";

    if (event.target.value) {
      if (
        event.target.value !== null &&
        event.target.value.MembershipTypeName &&
        event.target.value.MembershipTypeName !== null
      )
        strSelectedMembershipTypeName = event.target.value.MembershipTypeName;

      if (
        event.target.value !== null &&
        event.target.value.MembershipTypeValue !== null
      )
        strSelectedMembershipTypeValue = event.target.value.MembershipTypeValue;

      modelMembershipDetails.MembershipTypeDetails.SelectedMembershipTypeName =
        strSelectedMembershipTypeName;
      modelMembershipDetails.MembershipTypeDetails.SelectedMembershipTypeValue =
        strSelectedMembershipTypeValue;
    }
  };

  const onChangeMagazineSelection = (event) => {
    let strSelectedMagazineSelectionName = "";
    let strSelectedMagazineSelectionValue = "";
    if (event.target.value) {
      if (
        event.target.value !== null &&
        event.target.value.MagazineSelectionName &&
        event.target.value.MagazineSelectionName !== null
      )
        strSelectedMagazineSelectionName =
          event.target.value.MagazineSelectionName;

      if (
        event.target.value !== null &&
        event.target.value.MagazineSelectionValue &&
        event.target.value.MagazineSelectionValue !== null
      )
        strSelectedMagazineSelectionValue =
          event.target.value.MagazineSelectionValue;

      modelMagazineSelectionDetails.MagazineSelectionDetails.SelectedMagazineSelectionName =
        strSelectedMagazineSelectionName;
      modelMagazineSelectionDetails.MagazineSelectionDetails.SelectedMagazineSelectionValue =
        strSelectedMagazineSelectionValue;

      if (
        modelMagazineSelectionDetails.MagazineSelectionDetails
          .SelectedMagazineSelectionValue === "0"
      )
        setIsAddressInternational(false);
      else {
        if (
          modelAddressDetails.AddressDetails.Country === "United States" ||
          modelAddressDetails.AddressDetails.Country === "USA"
        )
          setIsAddressInternational(false);
        else if (modelAddressDetails.AddressDetails.Country === "Canada")
          setIsAddressInternational(true);
        else if (modelAddressDetails.AddressDetails.Country === "Mexico")
          setIsAddressInternational(true);
        else setIsAddressInternational(true);
      }
    }
  };

  const onChangeAreaDesignation = (event) => {
    let strSelectedAreaDesignationName = "";
    let strSelectedAreaDesignationValue = "";
    if (event.target.value) {
      if (
        event.target.value !== null &&
        event.target.value.AreaDesignationName &&
        event.target.value.AreaDesignationName !== null
      )
        strSelectedAreaDesignationName = event.target.value.AreaDesignationName;

      if (
        event.target.value !== null &&
        event.target.value.AreaDesignationValue &&
        event.target.value.AreaDesignationValue !== null
      )
        strSelectedAreaDesignationValue =
          event.target.value.AreaDesignationValue;

      modelAreaDesignationDetails.AreaDesignationDetails.SelectedAreaDesignationName =
        strSelectedAreaDesignationName;
      modelAreaDesignationDetails.AreaDesignationDetails.SelectedAreaDesignationValue =
        strSelectedAreaDesignationValue;
    }
  };

  const onChangeAnnualDonation = (event) => {
    let strSelectedAnnualDonationName = "";
    let strSelectedAnnualDonationValue = "";
    if (event.target.value) {
      if (
        event.target.value !== null &&
        event.target.value.AnnualDonationDetailsName &&
        event.target.value.AnnualDonationDetailsName !== null
      )
        strSelectedAnnualDonationName =
          event.target.value.AnnualDonationDetailsName;

      if (
        event.target.value !== null &&
        event.target.value.AnnualDonationDetailsValue &&
        event.target.value.AnnualDonationDetailsValue !== null
      )
        strSelectedAnnualDonationValue =
          event.target.value.AnnualDonationDetailsValue;

      modelAnnualDonationDetails.AnnualDonationDetails.SelectedAnnualDonationName =
        strSelectedAnnualDonationName;
      modelAnnualDonationDetails.AnnualDonationDetails.SelectedAnnualDonationValue =
        strSelectedAnnualDonationValue;
    }
  };

  const onChangeAnnualDonationAmount = (event) => {
    let iSelectedDonationAmount = "";
    if (event.target.value) {
      if (event.target.value !== null)
        iSelectedDonationAmount = event.target.value;

      modelAnnualDonationDetails.AnnualDonationDetails.SelectedAnnualDonationAmount =
        iSelectedDonationAmount;
    }
  };

  const onChangeAmateur = (event) => {
    let strSelectedAmateurValue = "";
    if (event.value !== null) strSelectedAmateurValue = event.value;

    modelAmateurDetails.AmateurTypeDetails.SelectedAmateurTypeValue =
      strSelectedAmateurValue;
  };

  const onChangeYoungRiderProgram = (event) => {
    let strSelectedYoungRiderProgramValue = "";
    if (event.value !== null) strSelectedYoungRiderProgramValue = event.value;

    modelYoungRiderProgramDetails.YoungRiderProgramDetails.SelectedYoungRiderProgramDetailsValue =
      strSelectedYoungRiderProgramValue;
  };

  const onChangeAdultRiderProgram = (event) => {
    let strSelectedAdultRiderProgramValue = "";
    if (event.value !== null) strSelectedAdultRiderProgramValue = event.value;

    modelAdultRiderProgramDetails.AdultRiderProgramDetails.SelectedAdultRiderProgramDetailsValue =
      strSelectedAdultRiderProgramValue;
  };

  const onChangeAutoRenewMembership = (event) => {
    let bSelectedAutoRenewMembershipValue = false;
    if (event.value !== null) bSelectedAutoRenewMembershipValue = event.value;

    modelAutoRenewMembershipDetails.AutoRenewMembershipDetails.SelectedAutoRenewMembership =
      bSelectedAutoRenewMembershipValue;
  };

  // Req C (2026-09-23): if the "Mailing Address" box's live text still matches the last
  // picked/prefilled one-line address, save `addr` as-is -- no Google call needed. If it
  // differs (member typed over it without picking a suggestion), ask Google to split it
  // (Geocoder -- confirmed via a real, read-only, headless-Chrome call against this app's own
  // API key that Geocoding is allowed for this key before relying on it, not assumed). Accepts
  // only a single, confident, complete result; otherwise does NOT save, shows the error, and
  // reveals the Edit fields so the member can fix it up by hand. Never guesses a partial parse.
  const resolveAddressForSave = () => {
    return new Promise((resolve) => {
      const boxText = inputRef.current ? inputRef.current.value.trim() : "";
      const lastKnownText = (addressDetailsValue || "").trim();

      if (boxText !== "" && boxText === lastKnownText) {
        resolve(addr);
        return;
      }

      if (boxText === "") {
        setDataConfirmAddressErrorMessage({
          ErrorMessage: "Please confirm the Address.",
        });
        resolve(null);
        return;
      }

      if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
        setDataConfirmAddressErrorMessage({
          ErrorMessage: "Please check the address or pick a suggestion.",
        });
        setShowEditFields(true);
        resolve(null);
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: boxText }, (results, status) => {
        if (status !== "OK" || !results || results.length !== 1) {
          // No result, or more than one candidate -- ambiguous. Do not guess.
          setDataConfirmAddressErrorMessage({
            ErrorMessage: "Please check the address or pick a suggestion.",
          });
          setShowEditFields(true);
          resolve(null);
          return;
        }

        const parsed = parseAddressComponents(results[0].address_components);

        if (!hasRequiredAddressParts(parsed)) {
          // Resolved to exactly one place, but it's missing a required part (e.g. no street,
          // or a US result with no state/zip) -- partial, still don't guess.
          setDataConfirmAddressErrorMessage({
            ErrorMessage: "Please check the address or pick a suggestion.",
          });
          setShowEditFields(true);
          resolve(null);
          return;
        }

        // Confident single, complete result -- accept it, same sync as place_changed.
        setAddr(parsed);
        modelAddressDetails.AddressDetails = { ...parsed };
        const displayText = buildOneLineAddress(parsed);
        setAddressDetailsValue(displayText);
        if (inputRef.current) inputRef.current.value = displayText;
        updateIsAddressInternational(parsed.Country);

        resolve(parsed);
      });
    });
  };

  const onSubmitSaveSettings = async (e) => {
    e.preventDefault();

    const resolvedAddr = await resolveAddressForSave();
    if (resolvedAddr === null) {
      // resolveAddressForSave already set the error message (and revealed Edit if needed) --
      // stop here, do not call formValidation/Save with a rejected/guessed address.
      return;
    }

    const isValid = formValidation(resolvedAddr);

    if (isValid) {
      // FIX 2026-09-30: auto-renewal cannot work without a usable card on file. Block the save
      // (instead of reporting "saved!") when auto-renewal is ON and the card on file is missing
      // or expired. Card status is "VALID" / "EXPIRED" / "NO CARD" from the API, and is set to
      // "VALID" by UpdateCreditCardOnFile after a successful card update on this page.
      const autoRenewOn =
        !!modelAutoRenewMembershipDetails.AutoRenewMembershipDetails &&
        !!modelAutoRenewMembershipDetails.AutoRenewMembershipDetails
          .SelectedAutoRenewMembership;
      const cardDetails =
        props.objModelMembershipDetails &&
        props.objModelMembershipDetails.CreditCardDetails;
      const cardStatus = cardDetails ? cardDetails.CreditCardExpirationStatus : "NO CARD";
      if (autoRenewOn && cardStatus !== "VALID") {
        alert(
          cardStatus === "EXPIRED"
            ? "Your card on file has expired. Please update your card before turning on auto-renewal."
            : "There is no card on file. Please add a card before turning on auto-renewal."
        );
        return;
      }

      console.log("submit details");
      console.log("Valid details to submit to post API");
      // FIX 2026-09-30: this call had no error handling - a failed request or an expired token
      // (MembershipSettingsPostData comes back null) crashed silently and the member saw nothing.
      callMembershipSettingsPost(resolvedAddr)
        .then((response) => {
          console.log("Call Membership Settings Post done");
          if (
            response &&
            response.MembershipSettingsPostData &&
            response.MembershipSettingsPostData.Message === "Success"
          ) {
            alert("Renewal Message settings saved!");
          } else {
            alert("Renewal Message settings not saved! Please check for errors and try again.");
          }
        })
        .catch((error) => {
          console.error("Membership settings save failed", error);
          alert(
            "Renewal Message settings could not be saved. Please try again. If this keeps happening, please sign out and sign in again."
          );
        });
    }
  };

  const formValidation = (addrOverride) => {
    const activeAddr = addrOverride || addr;
    const dataConfirmAddressErrorMessage = {};
    const dataMembershipTypeErrorMessage = {};
    const dataAmateurErrorMessage = {};
    const dataMagazineSelectionErrorMessage = {};
    const dataAreaDesignationErrorMessage = {};
    const dataYoungRiderProgramErrorMessage = {};
    const dataAdultRiderProgramErrorMessage = {};
    const dataAnnualDonationAmountErrorMessage = {};

    let isValid = true;

    console.log(props.objModelSelectedMembershipDetails);
    // validate Confirm Address (2026-09-23: Address1/City/Country always required; State+Zip
    // required only when Country is the US, matching Dev-Lead's spec item 7 -- shared with the
    // Save-time Google-split acceptance gate via hasRequiredAddressParts so the two can never
    // disagree)
    if (!hasRequiredAddressParts(activeAddr)) {
      dataConfirmAddressErrorMessage.ErrorMessage =
        "Please confirm the Address.";
      isValid = false;
    }

    // validate Membership Type
    if (
      modelMembershipDetails !== null &&
      modelMembershipDetails.MembershipTypeDetails
        .SelectedMembershipTypeValue === -1
    ) {
      dataMembershipTypeErrorMessage.ErrorMessage =
        "Please select the Membership Type.";
      isValid = false;
    }

    // validate Amateur Type
    if (
      modelAmateurDetails !== null &&
      modelAmateurDetails.AmateurTypeDetails.SelectedAmateurTypeValue === "-1"
    ) {
      dataAmateurErrorMessage.ErrorMessage =
        "Please select the Amateur status.";
      isValid = false;
    }

    // validate Magazine Selection
    if (
      modelMagazineSelectionDetails !== null &&
      modelMagazineSelectionDetails.MagazineSelectionDetails
        .SelectedMagazineSelectionValue === -1
    ) {
      dataMagazineSelectionErrorMessage.ErrorMessage =
        "Please select the Magazine selection.";
      isValid = false;
    }

    // validate Area Designation
    if (
      modelAreaDesignationDetails !== null &&
      modelAreaDesignationDetails.AreaDesignationDetails
        .SelectedAreaDesignationValue === "00000000-0000-0000-0000-000000000000"
    ) {
      dataAreaDesignationErrorMessage.ErrorMessage =
        "Please select the Area Designation for Awards & Programs.";
      isValid = false;
    }

    // validate Young Rider Program Options
    if (
      modelYoungRiderProgramDetails !== null &&
      modelYoungRiderProgramDetails.YoungRiderProgramDetails
        .SelectedYoungRiderProgramDetailsValue === ""
    ) {
      dataYoungRiderProgramErrorMessage.ErrorMessage =
        "Please select the Young Rider Program options.";
      isValid = false;
    }

    // validate Adult Rider Program Options
    if (
      modelAdultRiderProgramDetails !== null &&
      modelAdultRiderProgramDetails.AdultRiderProgramDetails
        .SelectedAdultRiderProgramDetailsValue === ""
    ) {
      dataAdultRiderProgramErrorMessage.ErrorMessage =
        "Please select the Adult Rider Program options.";
      isValid = false;
    }

    // validate Annual Donation
    if (
      modelAnnualDonationDetails !== null &&
      modelAnnualDonationDetails.AnnualDonationDetails
        .SelectedAnnualDonationValue !==
        "00000000-0000-0000-0000-000000000000" &&
      (modelAnnualDonationDetails.AnnualDonationDetails
        .SelectedAnnualDonationAmount === "" ||
        (modelAnnualDonationDetails.AnnualDonationDetails
          .SelectedAnnualDonationAmount !== "" &&
          parseInt(
            modelAnnualDonationDetails.AnnualDonationDetails
              .SelectedAnnualDonationAmount,
            10
          ) < 5))
    ) {
      dataAnnualDonationAmountErrorMessage.ErrorMessage =
        "Please enter the Donation Amount greater than $5.";
      isValid = false;
    }

    if (isValid) {
      console.log("Authenticated");
    } else {
      console.log("errors try again");
    }

    setDataConfirmAddressErrorMessage(dataConfirmAddressErrorMessage);
    setDataMembershipTypeErrorMessage(dataMembershipTypeErrorMessage);
    setDataAmateurErrorMessage(dataAmateurErrorMessage);
    setDataMagazineSElectionErrorMessage(dataMagazineSelectionErrorMessage);
    setDataAreaDesignationMonthErrorMessage(dataAreaDesignationErrorMessage);
    setDataYoungRiderProgramErrorMessage(dataYoungRiderProgramErrorMessage);
    setDataAdultRiderProgramErrorMessage(dataAdultRiderProgramErrorMessage);
    setDataAnnualDonationAmountErrorMessage(
      dataAnnualDonationAmountErrorMessage
    );

    return isValid;
  };

  const callMembershipSettingsPost = async (addrOverride) => {
    const activeAddr = addrOverride || addr;
    let strMembershipSettingsData = "";

    strMembershipSettingsData += "{";
    strMembershipSettingsData += '"UserID":"' + UserID + '"';

    if (activeAddr) {
      // 2026-09-23: build the AddressDetails block from the pure buildAddressDetailsJson()
      // function instead of hand-wrapping each value in quote characters. Any typed double-
      // quote or backslash (now possible since typed addresses are allowed, not just
      // Google-picked ones) previously broke the JSON outright; JSON.stringify escapes
      // correctly. Key names are unchanged (SelectedAddress1/2, SelectedCity, SelectedState,
      // SelectedZipcode, SelectedCountry).
      strMembershipSettingsData +=
        ',"AddressDetails":' + buildAddressDetailsJson(activeAddr);
    }

    if (
      modelMembershipDetails.MembershipTypeDetails &&
      modelMembershipDetails.MembershipTypeDetails !== null
    ) {
      strMembershipSettingsData += ',"MembershipTypeDetails":{';
      strMembershipSettingsData +=
        '"SelectedMembershipTypeName":"' +
        modelMembershipDetails.MembershipTypeDetails
          .SelectedMembershipTypeName +
        '"';
      strMembershipSettingsData +=
        ',"SelectedMembershipTypeValue":' +
        modelMembershipDetails.MembershipTypeDetails
          .SelectedMembershipTypeValue;
      strMembershipSettingsData += "}";
    }

    if (
      modelAmateurDetails.AmateurTypeDetails &&
      modelAmateurDetails.AmateurTypeDetails !== null
    ) {
      strMembershipSettingsData += ',"AmateurTypeDetails":{';
      strMembershipSettingsData +=
        '"SelectedAmateurTypeValue":' +
        modelAmateurDetails.AmateurTypeDetails.SelectedAmateurTypeValue;
      strMembershipSettingsData += "}";
    }

    if (
      modelMagazineSelectionDetails.MagazineSelectionDetails &&
      modelMagazineSelectionDetails.MagazineSelectionDetails !== null
    ) {
      strMembershipSettingsData += ',"MagazineSelectionDetails":{';
      strMembershipSettingsData +=
        '"SelectedMagazineSelectionName":"' +
        modelMagazineSelectionDetails.MagazineSelectionDetails
          .SelectedMagazineSelectionName +
        '"';
      strMembershipSettingsData +=
        ',"SelectedMagazineSelectionValue":' +
        modelMagazineSelectionDetails.MagazineSelectionDetails
          .SelectedMagazineSelectionValue;
      strMembershipSettingsData += "}";
    }

    if (
      modelAreaDesignationDetails.AreaDesignationDetails &&
      modelAreaDesignationDetails.AreaDesignationDetails !== null
    ) {
      if (
        modelAreaDesignationDetails.AreaDesignationDetails
          .SelectedAreaDesignationValue ==
        "00000000-0000-0000-0000-000000000000"
      ) {
        strMembershipSettingsData += ',"AreaDesignationDetails":{';
        strMembershipSettingsData +=
          '{"SelectedAreaDesignationName":"' +
          modelAreaDesignationDetails.AreaDesignationDetails
            .SelectedAreaDesignationValue +
          '"';
        strMembershipSettingsData += ',"SelectedAreaDesignationValue":""';
        strMembershipSettingsData += "}";
      } else {
        strMembershipSettingsData += ',"AreaDesignationDetails":{';
        strMembershipSettingsData +=
          '"SelectedAreaDesignationName":"' +
          modelAreaDesignationDetails.AreaDesignationDetails
            .SelectedAreaDesignationName +
          '"';
        strMembershipSettingsData +=
          ',"SelectedAreaDesignationValue":"' +
          modelAreaDesignationDetails.AreaDesignationDetails
            .SelectedAreaDesignationValue +
          '"';
      }
      strMembershipSettingsData += "}";
    }

    if (
      modelAnnualDonationDetails.AnnualDonationDetails &&
      modelAnnualDonationDetails.AnnualDonationDetails !== null
    ) {
      strMembershipSettingsData += ',"AnnualDonationDetails":{';
      if (
        modelAnnualDonationDetails.AnnualDonationDetails
          .SelectedAnnualDonationValue != "00000000-0000-0000-0000-000000000000"
      )
        strMembershipSettingsData +=
          '"SelectedAnnualDonationAmount":' +
          modelAnnualDonationDetails.AnnualDonationDetails
            .SelectedAnnualDonationAmount;
      else strMembershipSettingsData += '"SelectedAnnualDonationAmount":0';
      strMembershipSettingsData +=
        ',"SelectedAnnualDonationName":"' +
        modelAnnualDonationDetails.AnnualDonationDetails
          .SelectedAnnualDonationName +
        '"';
      strMembershipSettingsData +=
        ',"SelectedAnnualDonationValue":"' +
        modelAnnualDonationDetails.AnnualDonationDetails
          .SelectedAnnualDonationValue +
        '"';
      strMembershipSettingsData += "}";
    }

    if (
      modelAutoRenewMembershipDetails.AutoRenewMembershipDetails &&
      modelAutoRenewMembershipDetails.AutoRenewMembershipDetails !== null
    ) {
      strMembershipSettingsData += ',"AutoRenewMembershipDetails":{';

      strMembershipSettingsData += modelAutoRenewMembershipDetails
        .AutoRenewMembershipDetails.SelectedAutoRenewMembership
        ? '"SelectedAutoRenewMembership":1'
        : '"SelectedAutoRenewMembership":0';
      strMembershipSettingsData += "}";
    }

    let YoungRiderProgramIsAvailable = false;
    strMembershipSettingsData += ',"ProgramDetails":[';
    if (
      modelYoungRiderProgramDetails &&
      modelYoungRiderProgramDetails.YoungRiderProgramDetails &&
      modelYoungRiderProgramDetails.YoungRiderProgramDetails !== null &&
      modelYoungRiderProgramDetails.YoungRiderProgramDetails
        .SelectedYoungRiderProgramDetailsValue &&
      modelYoungRiderProgramDetails.YoungRiderProgramDetails
        .SelectedYoungRiderProgramDetailsValue !== null
    ) {
      if (
        modelYoungRiderProgramDetails.YoungRiderProgramDetails
          .SelectedYoungRiderProgramDetailsValue ===
        "00000000-0000-0000-0000-000000000000"
      ) {
        strMembershipSettingsData +=
          '{"AreaID":"' +
          modelAreaDesignationDetails.AreaDesignationDetails
            .SelectedAreaDesignationValue +
          '"';
        strMembershipSettingsData += ',"AreaMemberGroupTypeID":""';
        strMembershipSettingsData += "}";
      } else {
        strMembershipSettingsData +=
          '{"AreaID":"' +
          modelAreaDesignationDetails.AreaDesignationDetails
            .SelectedAreaDesignationValue +
          '"';
        strMembershipSettingsData +=
          ',"AreaMemberGroupTypeID":"' +
          modelYoungRiderProgramDetails.YoungRiderProgramDetails
            .SelectedYoungRiderProgramDetailsValue +
          '"';
        strMembershipSettingsData += "}";
      }
      YoungRiderProgramIsAvailable = true;
    }

    console.log(modelAdultRiderProgramDetails);
    if (
      modelAdultRiderProgramDetails &&
      modelAdultRiderProgramDetails.AdultRiderProgramDetails &&
      modelAdultRiderProgramDetails.AdultRiderProgramDetails !== null &&
      modelAdultRiderProgramDetails.AdultRiderProgramDetails
        .SelectedAdultRiderProgramDetailsValue &&
      modelAdultRiderProgramDetails.AdultRiderProgramDetails
        .SelectedAdultRiderProgramDetailsValue !== null
    ) {
      if (YoungRiderProgramIsAvailable) strMembershipSettingsData += ",";

      if (
        modelAdultRiderProgramDetails.AdultRiderProgramDetails
          .SelectedAdultRiderProgramDetailsValue ===
        "00000000-0000-0000-0000-000000000000"
      ) {
        strMembershipSettingsData +=
          '{"AreaID":"' +
          modelAreaDesignationDetails.AreaDesignationDetails
            .SelectedAreaDesignationValue +
          '"';
        strMembershipSettingsData += ',"AreaMemberGroupTypeID":""';
        strMembershipSettingsData += "}";
      } else {
        strMembershipSettingsData +=
          '{"AreaID":"' +
          modelAreaDesignationDetails.AreaDesignationDetails
            .SelectedAreaDesignationValue +
          '"';
        strMembershipSettingsData +=
          ',"AreaMemberGroupTypeID":"' +
          modelAdultRiderProgramDetails.AdultRiderProgramDetails
            .SelectedAdultRiderProgramDetailsValue +
          '"';
        strMembershipSettingsData += "}";
      }
    }

    strMembershipSettingsData += "]";
    strMembershipSettingsData += "}";
    console.log(strMembershipSettingsData);
    setData(strMembershipSettingsData);
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        AppId: appId,
        TokenId: localStorage.getItem("TokenId"),
        MembershipSettingsData: strMembershipSettingsData,
      }),
    };

    let postMembershipSettingsURLAPI =
      urlAPI + "OSPortalMembershipSettingsDetailsPost";
    const response = await fetch(postMembershipSettingsURLAPI, requestOptions);
    if (!response.ok) {
      throw new Error("Data Membership Settings Post cound not be fetched!");
      console.log(response);
    } else {
      console.log(response);
      return response.json();
    }
  };

  return (
    <Box sx={{ backgroundColor: "#F7F7F9", p: "25px 0" }}>
      <Box
        sx={{ maxWidth: "750px", margin: "0 auto" }}
        className="account-form"
      >
        <Card className="card_box">
          <form onSubmit={onSubmitSaveSettings}>
            <CardHeader
              className={classes.lblMembershipRenewalSettings}
              title="Auto-Renewal Settings"
              sx={{ textAlign: "center", pb: 2, fontWeight: "500 !important" }}
            />
            <Typography
              className={classes.lblSubMembershipRenewalSettings}
              variant="body2"
              sx={{
                fontWeight: 500,
                m: "0 15px !important",
                color: "#4c4e64de",
                textAlign: "center",
              }}
            >
              Update the options below to change your auto-renewal and
              quick-renewal default settings.
            </Typography>

            <Divider sx={{ m: "20px 0  !important" }} />

            <CardContent>
              <Grid container spacing={5}>
                <Grid item xs={12} sm={6}>
                  {props.objModelMembershipDetails != null &&
                  props.objModelMembershipDetails.MembershipDataDetails !=
                    null ? (
                    <div>
                      <div className={classes.lblMembershipTypeTop}>
                        Name :{" "}
                        {
                          props.objModelMembershipDetails.MembershipDataDetails
                            .MemberFullName
                        }
                      </div>
                      <div className={classes.lblMembershipTypeTop}>
                        DOB :{" "}
                        {
                          props.objModelMembershipDetails.MembershipDataDetails
                            .MemberDOB
                        }
                      </div>
                      <div className={classes.lblMembershipTypeTop}>
                        Member ID :{" "}
                        {
                          props.objModelMembershipDetails.MembershipDataDetails
                            .MemberID
                        }
                      </div>
                    </div>
                  ) : (
                    <div />
                  )}
                </Grid>
              </Grid>
            </CardContent>

            <Divider sx={{ m: "20px 0 !important" }} />

            <div>
              {props.objModelMembershipDetails != null &&
              props.objModelMembershipDetails.MembershipDataDetails != null &&
              props.objModelMembershipDetails.MembershipDataDetails
                .MemberLockStatus == 0 ? (
                <div>
                  <CardContent>
                    <Grid container spacing={5}>
                      {/* Req A (2026-09-23, Seetha's rejection of the previous layout): single
                          box, same look/markup pattern as Account Settings' "Mailing Address"
                          field (see accountSettings.js) -- plain <input className="form-control">
                          with the shared .phone-input/.phone-label/.req_field global CSS classes,
                          not a MUI TextField (MUI's floating-label needs more vertical space than
                          this page's old custom height override gave it, which is what caused
                          the overlapping labels in Seetha's screenshot). Label changed from
                          "Confirm Address Mailings" to "Mailing Address" to match Account
                          Settings exactly, per her explicit ask for that look. */}
                      <Grid item xs={12} sm={6} className="phone-input">
                        <label className="phone-label">
                          Mailing Address<span className="req_field">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          ref={inputRef}
                          defaultValue={addressDetailsValue || ""}
                          placeholder="Enter Location"
                        />
                        {Object.keys(dataConfirmAddressErrorMessage).map(
                          (key) => {
                            return (
                              <div
                                className={classes.lblErrors}
                                key={key}
                              >
                                {dataConfirmAddressErrorMessage[key]}
                              </div>
                            );
                          }
                        )}
                        <Link
                          href="#"
                          onClick={onToggleEditFields}
                          sx={{ fontSize: "0.85rem", mt: 1, display: "inline-block" }}
                        >
                          {showEditFields ? "Hide address fields" : "Edit"}
                        </Link>
                        {/* 2026-09-30 (Seetha): tell members Edit is only the fallback - search first. */}
                        {!showEditFields && (
                          <span
                            style={{
                              fontSize: "0.8rem",
                              color: "#666",
                              marginLeft: "6px",
                            }}
                          >
                            - Use only if your address doesn't appear when you search.
                          </span>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6} className="membership-type">
                        {props.objModelMembershipDetails != null &&
                        props.objModelMembershipDetails.MembershipTypeDetails !=
                          null &&
                        props.objModelMembershipDetails.MembershipTypeDetails
                          .MembershipTypeDataDetails != null ? (
                          <FormControl
                            className="gender-dropdown dropdown"
                            fullWidth
                          >
                            <DropDownList
                              label="Membership Type"
                              className={classes.drpDnMembership}
                              data={
                                props.objModelMembershipDetails
                                  .MembershipTypeDetails
                                  .MembershipTypeDataDetails
                              }
                              textField="MembershipTypeName"
                              valueField="MembershipTypeValue"
                              defaultValue={
                                props.objModelMembershipDetails
                                  .MembershipTypeDetails.MembershipTypeSelection
                              }
                              onChange={onChangeMembershipType}
                            ></DropDownList>
                          </FormControl>
                        ) : (
                          <FormControl
                            className="gender-dropdown dropdown"
                            fullWidth
                          >
                            <DropDownList
                              className={classes.drpDnMembership}
                              onChange={onChangeMembershipType}
                            />
                          </FormControl>
                        )}
                        {Object.keys(dataMembershipTypeErrorMessage).map(
                          (key) => {
                            return (
                              <div className={classes.lblErrors}>
                                {dataMembershipTypeErrorMessage[key]}
                              </div>
                            );
                          }
                        )}
                      </Grid>
                      {/* Req D (2026-09-23): "Edit" reveals these in their own neat section below
                          the box, in pairs, with normal MUI TextField spacing (fullWidth, no
                          custom height override) -- avoids the exact overlapping-label bug from
                          the previous layout. Prefilled from current `addr`; edits here win and
                          update the one-line box above (see onChangeAddressField). */}
                      {showEditFields && (
                        <Grid item xs={12}>
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Address 1"
                                value={addr.SelectedAddress1}
                                onChange={onChangeAddressField(
                                  "SelectedAddress1"
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Address 2 (optional)"
                                value={addr.SelectedAddress2}
                                onChange={onChangeAddressField(
                                  "SelectedAddress2"
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="City"
                                value={addr.SelectedCity}
                                onChange={onChangeAddressField("SelectedCity")}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="State/Province"
                                value={addr.SelectedState}
                                onChange={onChangeAddressField(
                                  "SelectedState"
                                )}
                                onBlur={onBlurAddressField("SelectedState")}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Zip/Postal Code"
                                value={addr.SelectedZipcode}
                                onChange={onChangeAddressField(
                                  "SelectedZipcode"
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label="Country"
                                value={addr.Country}
                                onChange={onChangeAddressField("Country")}
                                onBlur={onBlurAddressField("Country")}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>

                  <Divider sx={{ m: "20px 0 !important" }} />

                  <CardContent>
                    <Grid container spacing={5}>
                      <Grid item xs={12} sm={12}>
                        {props.objModelMembershipDetails != null &&
                        props.objModelMembershipDetails.AmateurTypeDetails !=
                          null &&
                        props.objModelMembershipDetails.AmateurTypeDetails
                          .AmateurDetailsSelection != null ? (
                          <div>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontSize: "16px !important",
                                color: "#4c4e64de",
                                m: "0!important",
                              }}
                            >
                              I am participating as a:
                            </Typography>
                            <RadioGroup
                              className={classes.lblAdditionalText}
                              data={
                                props.objModelMembershipDetails
                                  .AmateurTypeDetails.AmateurDataDetails
                              }
                              defaultValue={
                                props.objModelMembershipDetails
                                  .AmateurTypeDetails.AmateurDetailsSelection
                              }
                              onChange={onChangeAmateur}
                            />
                          </div>
                        ) : props.objModelMembershipDetails !== null &&
                          props.objModelMembershipDetails.AmateurTypeDetails !==
                            null ? (
                          <div>
                            <div className={classes.lblMembershipType}>
                              I am participating as a:
                            </div>
                            <RadioGroup
                              className={classes.lblAdditionalText}
                              data={
                                props.objModelMembershipDetails
                                  .AmateurTypeDetails.AmateurDataDetails
                              }
                              onChange={onChangeAmateur}
                            />
                          </div>
                        ) : (
                          <div />
                        )}
                        {Object.keys(dataAmateurErrorMessage).map((key) => {
                          return (
                            <div className={classes.lblErrors}>
                              {dataAmateurErrorMessage[key]}
                            </div>
                          );
                        })}
                      </Grid>
                    </Grid>
                  </CardContent>
                  <Divider sx={{ m: "0 0 20px !important" }} />

                  <CardContent>
                    <Grid container spacing={5}>
                      <Grid item xs={12} sm={6}>
                        <div className={classes.lblMembershipTypeTop}></div>
                        {props.objModelMembershipDetails != null &&
                        props.objModelMembershipDetails
                          .MagazineSelectionDetails != null &&
                        props.objModelMembershipDetails.MagazineSelectionDetails
                          .MagazineSelectionDataDetails != null ? (
                          <FormControl
                            className="gender-dropdown dropdown"
                            fullWidth
                          >
                            <DropDownList
                              className={classes.drpDnMembership}
                              label="Magazine Selection"
                              data={
                                props.objModelMembershipDetails
                                  .MagazineSelectionDetails
                                  .MagazineSelectionDataDetails
                              }
                              textField="MagazineSelectionName"
                              valueField="MagazineSelectionValue"
                              defaultValue={
                                props.objModelMembershipDetails
                                  .MagazineSelectionDetails.MagazineSelection
                              }
                              onChange={onChangeMagazineSelection}
                            ></DropDownList>
                          </FormControl>
                        ) : (
                          <FormControl
                            className="gender-dropdown dropdown"
                            fullWidth
                          >
                            <DropDownList
                              className={classes.drpDnMembership}
                              onChange={onChangeMagazineSelection}
                            ></DropDownList>
                          </FormControl>
                        )}
                        {Object.keys(dataMagazineSelectionErrorMessage).map(
                          (key) => {
                            return (
                              <div className={classes.lblErrors}>
                                {dataMagazineSelectionErrorMessage[key]}
                              </div>
                            );
                          }
                        )}
                        <Box sx={{ mt: 2 }}>
                          {isFirstAddressValue ? (
                            props.objModelMembershipDetails != null &&
                            props.objModelMembershipDetails.AddressDetails !==
                              null &&
                            props.objModelMembershipDetails.AddressDetails
                              .Country !== null &&
                            (props.objModelMembershipDetails.AddressDetails
                              .Country === "United States" ||
                              props.objModelMembershipDetails.AddressDetails
                                .Country === "USA") ? (
                              <div
                                className={
                                  classes.divAdditionalMagazineShipping
                                }
                              >
                                <div
                                  className={classes.divAdditionalShippingLeft}
                                >
                                  <span className={classes.lblAdditionalText}>
                                    Additional Magazine Shipping Fees
                                  </span>
                                </div>
                                <div
                                  className={classes.divAdditionalShippingRight}
                                >
                                  <span className={classes.lblAdditionalText}>
                                    {
                                      props.objModelMembershipDetails
                                        .MagazineSelectionDetails.MagazineUSCost
                                    }
                                  </span>
                                </div>
                              </div>
                            ) : isAddressInternational &&
                              props.objModelMembershipDetails !== null &&
                              props.objModelMembershipDetails.AddressDetails !==
                                null &&
                              props.objModelMembershipDetails.AddressDetails
                                .Country !== null &&
                              props.objModelMembershipDetails.AddressDetails
                                .Country === "Canada" ? (
                              <div>
                                <span className={classes.lblInternationAddress}>
                                  Additional shipping fees will apply for
                                  international address
                                </span>
                                <div
                                  className={
                                    classes.divAdditionalMagazineShipping
                                  }
                                >
                                  <div
                                    className={
                                      classes.divAdditionalShippingLeft
                                    }
                                  >
                                    <span className={classes.lblAdditionalText}>
                                      Additional Magazine Shipping Fees
                                    </span>
                                  </div>
                                  <div
                                    className={
                                      classes.divAdditionalShippingRight
                                    }
                                  >
                                    <span className={classes.lblAdditionalText}>
                                      {
                                        props.objModelMembershipDetails
                                          .MagazineSelectionDetails
                                          .MagazineCanadaCost
                                      }
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ) : isAddressInternational &&
                              props.objModelMembershipDetails !== null &&
                              props.objModelMembershipDetails.AddressDetails !==
                                null &&
                              props.objModelMembershipDetails.AddressDetails
                                .Country !== null &&
                              props.objModelMembershipDetails.AddressDetails
                                .Country === "Mexico" ? (
                              <div>
                                <span className={classes.lblInternationAddress}>
                                  Additional shipping fees will apply for
                                  international address
                                </span>
                                <div
                                  className={
                                    classes.divAdditionalMagazineShipping
                                  }
                                >
                                  <div
                                    className={
                                      classes.divAdditionalShippingLeft
                                    }
                                  >
                                    <span className={classes.lblAdditionalText}>
                                      Additional Magazine Shipping Fees
                                    </span>
                                  </div>
                                  <div
                                    className={
                                      classes.divAdditionalShippingRight
                                    }
                                  >
                                    <span className={classes.lblAdditionalText}>
                                      {
                                        props.objModelMembershipDetails
                                          .MagazineSelectionDetails
                                          .MagazineMexicoCost
                                      }
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ) : isAddressInternational ? (
                              <div>
                                <span className={classes.lblInternationAddress}>
                                  Additional shipping fees will apply for
                                  international address
                                </span>
                                <div
                                  className={
                                    classes.divAdditionalMagazineShipping
                                  }
                                >
                                  <div
                                    className={
                                      classes.divAdditionalShippingLeft
                                    }
                                  >
                                    <span className={classes.lblAdditionalText}>
                                      Additional Magazine Shipping Fees
                                    </span>
                                  </div>
                                  <div
                                    className={
                                      classes.divAdditionalShippingRight
                                    }
                                  >
                                    <span className={classes.lblAdditionalText}>
                                      {
                                        props.objModelMembershipDetails
                                          .MagazineSelectionDetails
                                          .MagazineOthersCost
                                      }
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div />
                            )
                          ) : modelAddressDetails != null &&
                            modelAddressDetails.AddressDetails !== null &&
                            modelAddressDetails.AddressDetails.Country !==
                              null &&
                            (modelAddressDetails.AddressDetails.Country ===
                              "United States" ||
                              modelAddressDetails.AddressDetails.Country ===
                                "USA") ? (
                            <div
                              className={classes.divAdditionalMagazineShipping}
                            >
                              <div
                                className={classes.divAdditionalShippingLeft}
                              >
                                <span className={classes.lblAdditionalText}>
                                  Additional Magazine Shipping Fees
                                </span>
                              </div>
                              <div
                                className={classes.divAdditionalShippingRight}
                              >
                                <span className={classes.lblAdditionalText}>
                                  {
                                    props.objModelMembershipDetails
                                      .MagazineSelectionDetails.MagazineUSCost
                                  }
                                </span>
                              </div>
                            </div>
                          ) : isAddressInternational &&
                            modelAddressDetails !== null &&
                            modelAddressDetails.AddressDetails !== null &&
                            modelAddressDetails.AddressDetails.Country !==
                              null &&
                            modelAddressDetails.AddressDetails.Country ===
                              "Canada" ? (
                            <div>
                              <span className={classes.lblInternationAddress}>
                                Additional shipping fees will apply for
                                international address
                              </span>
                              <div
                                className={
                                  classes.divAdditionalMagazineShipping
                                }
                              >
                                <div
                                  className={classes.divAdditionalShippingLeft}
                                >
                                  <span className={classes.lblAdditionalText}>
                                    Additional Magazine Shipping Fees
                                  </span>
                                </div>
                                <div
                                  className={classes.divAdditionalShippingRight}
                                >
                                  <span className={classes.lblAdditionalText}>
                                    {
                                      props.objModelMembershipDetails
                                        .MagazineSelectionDetails
                                        .MagazineCanadaCost
                                    }
                                  </span>
                                </div>
                              </div>
                            </div>
                          ) : isAddressInternational &&
                            modelAddressDetails !== null &&
                            modelAddressDetails.AddressDetails !== null &&
                            modelAddressDetails.AddressDetails.Country !==
                              null &&
                            modelAddressDetails.AddressDetails.Country ===
                              "Mexico" ? (
                            <div>
                              <span className={classes.lblInternationAddress}>
                                Additional shipping fees will apply for
                                international address
                              </span>
                              <div
                                className={
                                  classes.divAdditionalMagazineShipping
                                }
                              >
                                <div
                                  className={classes.divAdditionalShippingLeft}
                                >
                                  <span className={classes.lblAdditionalText}>
                                    Additional Magazine Shipping Fees
                                  </span>
                                </div>
                                <div
                                  className={classes.divAdditionalShippingRight}
                                >
                                  <span className={classes.lblAdditionalText}>
                                    {
                                      props.objModelMembershipDetails
                                        .MagazineSelectionDetails
                                        .MagazineMexicoCost
                                    }
                                  </span>
                                </div>
                              </div>
                            </div>
                          ) : isAddressInternational ? (
                            <div>
                              <span className={classes.lblInternationAddress}>
                                Additional shipping fees will apply for
                                international address
                              </span>
                              <div
                                className={
                                  classes.divAdditionalMagazineShipping
                                }
                              >
                                <div
                                  className={classes.divAdditionalShippingLeft}
                                >
                                  <span className={classes.lblAdditionalText}>
                                    Additional Magazine Shipping Fees
                                  </span>
                                </div>
                                <div
                                  className={classes.divAdditionalShippingRight}
                                >
                                  <span className={classes.lblAdditionalText}>
                                    {
                                      props.objModelMembershipDetails
                                        .MagazineSelectionDetails
                                        .MagazineOthersCost
                                    }
                                  </span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div />
                          )}
                        </Box>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        {props.objModelMembershipDetails !== null &&
                        props.objModelMembershipDetails
                          .AreaDesignationDetails !== null &&
                        props.objModelMembershipDetails.AreaDesignationDetails
                          .AreaDesignationDataDetails !== null ? (
                          <FormControl
                            className="gender-dropdown dropdown"
                            fullWidth
                          >
                            <DropDownList
                              className={classes.drpDnMembership}
                              label="Area Designation for Awards & Programs"
                              data={
                                props.objModelMembershipDetails
                                  .AreaDesignationDetails
                                  .AreaDesignationDataDetails
                              }
                              textField="AreaDesignationName"
                              valueField="AreaDesignationValue"
                              defaultValue={
                                props.objModelMembershipDetails
                                  .AreaDesignationDetails
                                  .AreaDesignationSelection
                              }
                              onChange={onChangeAreaDesignation}
                            ></DropDownList>
                          </FormControl>
                        ) : (
                          <FormControl
                            className="gender-dropdown dropdown"
                            fullWidth
                          >
                            <DropDownList
                              className={classes.drpDnMembership}
                              onChange={onChangeAreaDesignation}
                            ></DropDownList>
                          </FormControl>
                        )}
                        {Object.keys(dataAreaDesignationErrorMessage).map(
                          (key) => {
                            return (
                              <div className={classes.lblErrors}>
                                {dataAreaDesignationErrorMessage[key]}
                              </div>
                            );
                          }
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                  <Divider sx={{ m: "20px 0 !important" }} />

                  <CardContent>
                    <Grid container spacing={5}>
                      <Grid item xs={12} sm={12}>
                        {props.objModelMembershipDetails !== null &&
                        props.objModelMembershipDetails
                          .YoungRiderProgramDetails !== null &&
                        props.objModelMembershipDetails.YoungRiderProgramDetails
                          .YoungRiderProgramDetailsSelection !== null ? (
                          <div className="radio_btn">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontSize: "16px !important",
                                color: "#4c4e64de",
                                m: "0!important",
                              }}
                            >
                              Young Rider Program Options
                            </Typography>
                            <RadioGroup
                              className={classes.lblAdditionalText}
                              data={
                                props.objModelMembershipDetails
                                  .YoungRiderProgramDetails
                                  .YoungRiderProgramDataDetails
                              }
                              defaultValue={
                                props.objModelMembershipDetails
                                  .YoungRiderProgramDetails
                                  .YoungRiderProgramDetailsSelection
                              }
                              onChange={onChangeYoungRiderProgram}
                            />
                          </div>
                        ) : props.objModelMembershipDetails !== null &&
                          props.objModelMembershipDetails
                            .YoungRiderProgramDetails !== null ? (
                          <div className="radio_btn">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontSize: "16px !important",
                                color: "#4c4e64de",
                                m: "0!important",
                              }}
                            >
                              Young Rider Program Options
                            </Typography>
                            <RadioGroup
                              className={classes.lblAdditionalText}
                              data={
                                props.objModelMembershipDetails
                                  .YoungRiderProgramDetails
                                  .YoungRiderProgramDataDetails
                              }
                              onChange={onChangeYoungRiderProgram}
                            />
                          </div>
                        ) : (
                          <div />
                        )}
                        {Object.keys(dataYoungRiderProgramErrorMessage).map(
                          (key) => {
                            return (
                              <div className={classes.lblErrors}>
                                {dataYoungRiderProgramErrorMessage[key]}
                              </div>
                            );
                          }
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                  <Divider sx={{ m: "0 0 20px !important" }} />

                  <CardContent>
                    <Grid container spacing={5}>
                      <Grid item xs={12} sm={6}>
                        {props.objModelMembershipDetails != null &&
                        props.objModelMembershipDetails.AnnualDonationDetails !=
                          null &&
                        props.objModelMembershipDetails.AnnualDonationDetails
                          .AnnualDonationDataDetails != null ? (
                          <FormControl
                            className="gender-dropdown dropdown"
                            fullWidth
                          >
                            <DropDownList
                              className={classes.drpDnMembership}
                              label="Annual Donation"
                              data={
                                props.objModelMembershipDetails
                                  .AnnualDonationDetails
                                  .AnnualDonationDataDetails
                              }
                              textField="AnnualDonationDetailsName"
                              valueField="AnnualDonationDetailsValue"
                              defaultValue={
                                props.objModelMembershipDetails
                                  .AnnualDonationDetails
                                  .AnnualDonationDetailsSelection
                              }
                              onChange={onChangeAnnualDonation}
                            ></DropDownList>
                          </FormControl>
                        ) : (
                          <FormControl
                            className="gender-dropdown dropdown"
                            fullWidth
                          >
                            <DropDownList
                              className={classes.drpDnMembership}
                              onChange={onChangeAnnualDonation}
                            ></DropDownList>
                          </FormControl>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <div className={classes.divAnnualDonationDetails}>
                          <span>$ </span>
                          <NumericTextBox
                            className={classes.nboxAnnualDonation}
                            defaultValue={
                              props.objModelMembershipDetails
                                .AnnualDonationDetails.AnnualDonationAmount
                                ? props.objModelMembershipDetails
                                    .AnnualDonationDetails.AnnualDonationAmount
                                : null
                            }
                            min={5}
                            max={5000}
                            step={5}
                            onChange={onChangeAnnualDonationAmount}
                          />
                        </div>
                        {Object.keys(dataAnnualDonationAmountErrorMessage).map(
                          (key) => {
                            return (
                              <div className={classes.lblErrors}>
                                {dataAnnualDonationAmountErrorMessage[key]}
                              </div>
                            );
                          }
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                  <Divider sx={{ m: "20px 0 !important" }} />

                  <CardContent>
                    <Grid container spacing={5}>
                      <Grid item xs={12} sm={12}>
                        <Typography
                          className={classes.lblMembershipType}
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: "16px !important",
                            color: "#4c4e64de",
                            m: "0 0 10px !important",
                          }}
                        >
                          {
                            props.objModelMembershipDetails
                              .AutoRenewMembershipDetails
                              .AutoRenewMembershipTitle
                          }
                        </Typography>
                        <FormControlLabel
                          className="membership_type"
                          label={
                            props.objModelMembershipDetails
                              .AutoRenewMembershipDetails
                              .AutoRenewMembershipName
                          }
                          control={
                            <Checkbox
                              defaultValue={
                                props.objModelMembershipDetails
                                  .AutoRenewMembershipDetails
                                  .AutoRenewMembershipValue
                              }
                              onChange={onChangeAutoRenewMembership}
                            />
                          }
                        />
                      </Grid>
                    </Grid>
                  </CardContent>

                  <Divider sx={{ m: "20px 0 0 !important" }} />
                  <CardContent>
                    <Grid container spacing={5}>
                      <Grid item xs={12} sm={12}>
                        <Typography
                          className={classes.lblMembershipType}
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: "16px !important",
                            color: "#4c4e64de",
                            m: "0 0 10px !important",
                            textAlign: "center",
                          }}
                        >
                          Credit Card On File
                        </Typography>
                        <div className={classes.divCreditCardDetails}>
                          {(() => {
                            if (
                              !isPaymentDone &&
                              props.objModelMembershipDetails.CreditCardDetails
                                .CreditCardExpirationStatus == "NO CARD"
                            )
                              return (
                                <Typography
                                  className={classes.divNoCreditCardOnFile}
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "16px !important",
                                    color: "blue !important",
                                    m: "0 0 10px !important",
                                    textAlign: "left",
                                  }}
                                >
                                  NO CARD ON FILE
                                </Typography>
                              );
                            else if (
                              !isPaymentDone &&
                              props.objModelMembershipDetails.CreditCardDetails
                                .CreditCardExpirationStatus == "EXPIRED"
                            )
                              return (
                                <div
                                  className={classes.divCreditCardOnFileExpired}
                                >
                                  <div
                                    className={classes.divCreditCardOnFileLeft}
                                  >
                                    <span>
                                      {
                                        props.objModelMembershipDetails
                                          .CreditCardDetails.CreditCardType
                                      }{" "}
                                      #
                                      {
                                        props.objModelMembershipDetails
                                          .CreditCardDetails.CreditCardNo
                                      }
                                    </span>
                                  </div>
                                  <div
                                    className={classes.divCreditCardOnFileRight}
                                  >
                                    <span>
                                      {
                                        props.objModelMembershipDetails
                                          .CreditCardDetails
                                          .CreditCardExpirationMonth
                                      }
                                      /
                                      {
                                        props.objModelMembershipDetails
                                          .CreditCardDetails
                                          .CreditCardExpirationYear
                                      }
                                    </span>
                                    <span>
                                      {" "}
                                      (
                                      {
                                        props.objModelMembershipDetails
                                          .CreditCardDetails
                                          .CreditCardExpirationStatus
                                      }
                                      )
                                    </span>
                                  </div>
                                </div>
                              );
                            else if (
                              !isPaymentDone &&
                              props.objModelMembershipDetails.CreditCardDetails
                                .CreditCardExpirationStatus == "VALID"
                            )
                              return (
                                <div
                                  className={
                                    classes.divCreditCardOnFileNotExpired
                                  }
                                >
                                  <div
                                    className={classes.divCreditCardOnFileLeft}
                                  >
                                    <span>
                                      {
                                        props.objModelMembershipDetails
                                          .CreditCardDetails.CreditCardType
                                      }{" "}
                                      #
                                      {
                                        props.objModelMembershipDetails
                                          .CreditCardDetails.CreditCardNo
                                      }
                                    </span>
                                  </div>
                                  <div
                                    className={classes.divCreditCardOnFileRight}
                                  >
                                    <span>
                                      {
                                        props.objModelMembershipDetails
                                          .CreditCardDetails
                                          .CreditCardExpirationMonth
                                      }
                                      /
                                      {
                                        props.objModelMembershipDetails
                                          .CreditCardDetails
                                          .CreditCardExpirationYear
                                      }
                                    </span>
                                  </div>
                                </div>
                              );
                            else if (isPaymentDone)
                              return (
                                <div
                                  className={
                                    classes.divCreditCardOnFileNotExpired
                                  }
                                >
                                  <div
                                    className={classes.divCreditCardOnFileLeft}
                                  >
                                    <span>
                                      {
                                        props.objModelMembershipDetails
                                          .CreditCardDetails.CreditCardType
                                      }{" "}
                                      #
                                      {
                                        props.objModelMembershipDetails
                                          .CreditCardDetails.CreditCardNo
                                      }
                                    </span>
                                  </div>
                                  <div
                                    className={classes.divCreditCardOnFileRight}
                                  >
                                    <span>
                                      {
                                        props.objModelMembershipDetails
                                          .CreditCardDetails
                                          .CreditCardExpirationMonth
                                      }
                                      /
                                      {
                                        props.objModelMembershipDetails
                                          .CreditCardDetails
                                          .CreditCardExpirationYear
                                      }
                                    </span>
                                  </div>
                                </div>
                              );
                          })()}
                        </div>
                        <div className="addpymennt_btn">
                          {isShowCardDetailsPopup ? (
                            <Button
                              themeColor={"primary"}
                              iconClass="fa fa-solid fa-minus"
                              onClick={onCardDetailsClick}
                            >
                              Add/Update Payment
                            </Button>
                          ) : (
                            <Button
                              themeColor={"primary"}
                              iconClass="fa fa-solid fa-plus"
                              onClick={onCardDetailsClick}
                            >
                              Add/Update Payment
                            </Button>
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <UpdateCreditCardOnFile
                          UserID={UserID}
                          objModelMembershipDetails={
                            props.objModelMembershipDetails
                          }
                          objModelSelectedMembershipDetails={
                            props.objModelSelectedMembershipDetails
                          }
                          isShowCardDetailsPopup={isShowCardDetailsPopup}
                          updateShowCardDetailsPopup={
                            updateShowCardDetailsPopup
                          }
                          isPaymentDone={isPaymentDone}
                          updateIsPaymentDone={updateIsPaymentDone}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>

                  <Divider sx={{ m: "0 !important" }} />
                  <div className={classes.divSaveSettings}>
                    <Button
                      className="renewal_btn"
                      sx={{
                        backgroundColor: "#000",
                        color: "#fff",
                        lineHeight: 2,
                      }}
                      type="Submit"
                    >
                      SAVE SETTINGS
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <br />
                  <span className={classes.lblAutoRenewalSettings}>
                    This membership account is currently undergoing updates and
                    maintenance. If you have any questions, please contact the
                    office at 703-779-0440 (1) or{" "}
                    <u>
                      <a
                        href="mailto:support@useventing.com"
                        className={classes.lblAutoRenewalSettings}
                      >
                        support@useventing.com
                      </a>
                    </u>
                    .
                  </span>
                </div>
              )}
            </div>
          </form>
        </Card>
      </Box>
    </Box>
  );
};

export default MembershipSettings;
